'use client';

import { useEffect } from 'react';
import { logger } from '@/lib/logger';

export default function ErrorPage({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  useEffect(() => {
    // Log the error to an error reporting service
    logger.error('Application error', {
      error: error.message,
      stack: error.stack,
      digest: error.digest,
    });
  }, [error]);

  // Check if this is an environment validation error
  const isEnvError = error.message?.includes('Environment validation failed');

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        textAlign: 'center',
        padding: '2rem',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        backgroundColor: '#f5f5f5',
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '3rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          maxWidth: '600px',
          width: '100%',
        }}
      >
        <h2
          style={{
            fontSize: '1.5rem',
            marginBottom: '1rem',
            color: isEnvError ? '#d32f2f' : '#333',
          }}
        >
          {isEnvError ? 'Configuration Error' : 'Something went wrong!'}
        </h2>

        {isEnvError ? (
          <>
            <p style={{ marginBottom: '1.5rem', color: '#666', lineHeight: '1.5' }}>
              The application is not properly configured. Required environment variables are missing.
            </p>

            {process.env.NODE_ENV === 'production' ? (
              <div
                style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '4px',
                  padding: '1rem',
                  marginBottom: '2rem',
                  textAlign: 'left',
                }}
              >
                <p style={{ margin: 0, color: '#856404', fontSize: '0.875rem' }}>
                  <strong>For System Administrators:</strong>
                  <br />
                  Please ensure all required environment variables are configured in AWS Amplify:
                </p>
                <ul
                  style={{
                    marginTop: '0.5rem',
                    marginBottom: 0,
                    paddingLeft: '1.5rem',
                    color: '#856404',
                    fontSize: '0.875rem',
                  }}
                >
                  <li>SESSION_SECRET</li>
                  <li>NEXT_PUBLIC_MEDPLUM_BASE_URL</li>
                  <li>NEXT_PUBLIC_MEDPLUM_CLIENT_ID</li>
                  <li>NEXT_PUBLIC_MEDPLUM_PROJECT_ID</li>
                  <li>NEXT_PUBLIC_APP_URL</li>
                </ul>
              </div>
            ) : (
              <div
                style={{
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  padding: '1rem',
                  marginBottom: '2rem',
                  textAlign: 'left',
                }}
              >
                <p style={{ margin: 0, color: '#666', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                  <strong>Developer Information:</strong>
                </p>
                <pre
                  style={{
                    margin: 0,
                    fontSize: '0.75rem',
                    color: '#d32f2f',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {error.message}
                </pre>
                <p
                  style={{
                    margin: '0.5rem 0 0 0',
                    color: '#666',
                    fontSize: '0.75rem',
                  }}
                >
                  Check your .env.local file. See .env.example for reference.
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <p style={{ marginBottom: '2rem', color: '#666' }}>An error occurred while loading this page.</p>
            {process.env.NODE_ENV === 'development' && (
              <details
                style={{
                  textAlign: 'left',
                  marginBottom: '2rem',
                  fontSize: '0.875rem',
                  color: '#666',
                }}
              >
                <summary style={{ cursor: 'pointer', marginBottom: '0.5rem' }}>Error details</summary>
                <pre
                  style={{
                    backgroundColor: '#f5f5f5',
                    padding: '1rem',
                    borderRadius: '4px',
                    overflow: 'auto',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {error.message}
                  {error.stack && `\n\n${error.stack}`}
                </pre>
              </details>
            )}
          </>
        )}

        <button
          type="button"
          onClick={reset}
          style={{
            display: 'inline-block',
            padding: '0.75rem 1.5rem',
            backgroundColor: isEnvError ? '#666' : '#0066cc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '1rem',
            cursor: isEnvError ? 'not-allowed' : 'pointer',
            opacity: isEnvError ? 0.6 : 1,
          }}
          disabled={isEnvError}
        >
          {isEnvError ? 'Cannot Retry - Configuration Required' : 'Try again'}
        </button>
      </div>
    </div>
  );
}
